'use client';

import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Box, Button, GlobalStyles, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { shutdownIntercomMessenger } from '@/configurations/intercom/intercom-shutdown';

import {
    IMPERSONATION_BANNER_HEIGHT,
    IMPERSONATION_BANNER_HEIGHT_VAR,
} from './impersonation-banner-height';

/**
 * La barre elle-même. Non dismissible par construction : son objet est qu'on ne puisse pas
 * oublier qu'on agit au nom de quelqu'un d'autre.
 *
 * Le cadre ambre en `position: fixed; inset: 0` est en `pointerEvents: 'none'` — il borde la
 * fenêtre sans jamais intercepter un clic.
 */
export function ImpersonationBannerClient({ subject }: { subject: string }) {
    const [exiting, setExiting] = useState(false);

    async function exit() {
        setExiting(true);
        try {
            const response = await fetch('/api/auth/impersonate/exit', { method: 'POST' });
            const body = (await response.json()) as { data?: { adminUrl?: string } };
            // Même raison que la navigation complète ci-dessous : la session du messenger
            // appartient à l'utilisateur impersoné, et le support garde le chat pendant qu'il
            // impersone — l'admin ne doit pas repartir avec ses cookies Intercom.
            shutdownIntercomMessenger();
            // Navigation complète, jamais un router push : les payloads RSC déjà en cache
            // appartiennent à l'utilisateur impersoné.
            window.location.assign(body.data?.adminUrl ?? '/');
        } catch {
            setExiting(false);
        }
    }

    return (
        <>
            <GlobalStyles
                styles={{
                    ':root': { [IMPERSONATION_BANNER_HEIGHT_VAR]: IMPERSONATION_BANNER_HEIGHT },
                }}
            />
            <Box
                sx={(theme) => ({
                    position: 'fixed',
                    inset: 0,
                    pointerEvents: 'none',
                    border: `3px solid ${theme.palette.warning.main}`,
                    zIndex: theme.zIndex.appBar + 1,
                })}
            />
            <Box
                component="aside"
                aria-label="Impersonation"
                sx={(theme) => ({
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: IMPERSONATION_BANNER_HEIGHT,
                    bgcolor: theme.palette.warning.main,
                    color: theme.palette.warning.contrastText,
                    zIndex: theme.zIndex.appBar + 1,
                })}
            >
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        px: 2,
                    }}
                >
                    <VisibilityRoundedIcon fontSize="small" />
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                        }}
                    >
                        Acting as {subject}
                    </Typography>
                    <Typography variant="body2">Everything you do is recorded as them.</Typography>
                    <Button
                        size="small"
                        color="inherit"
                        variant="outlined"
                        startIcon={<LogoutRoundedIcon />}
                        disabled={exiting}
                        onClick={() => void exit()}
                        aria-label="Exit impersonation"
                    >
                        Exit
                    </Button>
                </Stack>
            </Box>
        </>
    );
}
